import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FileText, Save, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { contentAPI } from '../services/api';
import { hasAdminPermission } from '../utils/adminPermissions';

export const CONTENT_TYPES = ['terms', 'privacy', 'about', 'help'];
export const CONTENT_LANGUAGE = 'en';
export const getContentKey = (contentType) => `${contentType}_${CONTENT_LANGUAGE}`;
const normalizeContentPayload = (payload) => {
  const data = payload?.data ?? payload;
  const map = {};

  if (Array.isArray(data)) {
    data.forEach((item) => {
      if (item?.key) {
        map[item.key] = item.content || '';
      }
    });
    return map;
  }

  if (data && typeof data === 'object') {
    Object.entries(data).forEach(([key, value]) => {
      map[key] = typeof value === 'object' && value !== null ? value.content || '' : value || '';
    });
  }

  return map;
};

export default function AppContentManagement() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const hasPermission = hasAdminPermission(user, 'manage_content');

  const [activeTab, setActiveTab] = useState('terms');
  const [contents, setContents] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedKey, setSavedKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const currentKey = getContentKey(activeTab);

  const tabOptions = useMemo(() => CONTENT_TYPES.map((key) => ({
    key,
    label: t(`appContentManagement.tabs.${key}`)
  })), [t]);

  const loadContents = useCallback(async () => {
    if (!hasPermission) return;
    try {
      setLoading(true);
      setError(null);
      const res = await contentAPI.list();
      setContents(normalizeContentPayload(res.data));
    } catch (err) {
      setError(err.response?.data?.message || t('appContentManagement.errors.load'));
    } finally {
      setLoading(false);
    }
  }, [hasPermission, t]);

  useEffect(() => {
    loadContents();
  }, [loadContents]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await contentAPI.update(currentKey, contents[currentKey] || '');
      const savedContent = res.data?.data?.content ?? contents[currentKey] ?? '';
      setContents((prev) => ({ ...prev, [currentKey]: savedContent }));
      setSavedKey(currentKey);
      await loadContents();
      setTimeout(() => setSavedKey(null), 1500);
    } catch (err) {
      setError(err.response?.data?.message || t('appContentManagement.errors.save'));
    } finally {
      setSaving(false);
    }
  };

  if (!hasPermission) return null;

  const activeTabLabel = tabOptions.find((tab) => tab.key === activeTab)?.label;

  return (
    <div className="space-y-6 p-8">
      <div>
        <h1 className="flex items-center gap-3 text-3xl font-bold">
          <FileText size={32} className="text-cyan-400" />
          {t('appContentManagement.title')}
        </h1>
        <p className="mt-1 text-sm text-slate-400">{t('appContentManagement.subtitle')}</p>
      </div>

      <div className="rounded-2xl border border-white/5 bg-white/5 p-6">
        <div className="mb-6 flex gap-1 border-b border-white/10">
          {tabOptions.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`relative px-5 py-3 text-sm font-bold transition-colors ${
                activeTab === tab.key ? 'text-cyan-400' : 'text-slate-500 hover:text-slate-300'
              }`}
            >
              {tab.label}
              {activeTab === tab.key && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.5)]" />
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-8 text-center text-sm text-slate-500">{t('appContentManagement.loading')}</div>
        ) : (
          <div className="space-y-4">
            {error && (
              <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-300">
                {error}
              </div>
            )}
            <textarea
              value={contents[currentKey] || ''}
              onChange={(event) => setContents((prev) => ({ ...prev, [currentKey]: event.target.value }))}
              rows={16}
              className="w-full resize-none rounded-xl border border-white/10 bg-white/5 px-4 py-3 font-mono text-sm text-white placeholder-slate-500 focus:border-cyan-400/50 focus:outline-none"
              placeholder={t('appContentManagement.placeholder', {
                tab: activeTabLabel,
                language: t('languages.en', { defaultValue: 'English' })
              })}
            />
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 rounded-xl bg-cyan-400 px-6 py-3 font-black text-slate-900 transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-50"
              >
                {saving ? t('appContentManagement.saving') : <><Save size={16} />{t('appContentManagement.save')}</>}
              </button>
              {savedKey === currentKey && (
                <span className="flex items-center gap-1 text-sm font-bold text-emerald-400">
                  <Check size={14} />{t('appContentManagement.saved')}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
